import React, { useEffect, useState } from 'react';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, orderBy, serverTimestamp, doc, getDoc, onSnapshot, deleteDoc, updateDoc, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { Typography, TextField, Button, Card, CardContent, List, Box, ListItem, ListItemText, IconButton, CircularProgress, Select, MenuItem, FormControl, InputLabel, Collapse, Stack, Chip } from '@mui/material';
import Grid from '@mui/material/Grid'; // Importação correta para Grid v2
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from '../styles/Dashboard.module.css';

const Reclamacoes = () => {
  const [reclamacoes, setReclamacoes] = useState([]);
  const [novaReclamacao, setNovaReclamacao] = useState({ titulo: '', descricao: '', prioridade: 'Baixa' });
  const [imagem, setImagem] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [uploading, setUploading] = useState(false);
  const [moradoresMap, setMoradoresMap] = useState({});
  const [comentariosVisiveis, setComentariosVisiveis] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [novoComentario, setNovoComentario] = useState('');
  const statuses = ['Aberta', 'Em Análise', 'Respondida', 'Finalizada', 'Cancelada'];
  const prioridades = ['Baixa', 'Média', 'Alta'];
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [filtroData, setFiltroData] = useState(null);

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Efeito para buscar os moradores e criar um mapa de ID -> Nome
  useEffect(() => {
    if (!condominioId) return;

    const fetchMoradores = async () => {
      const q = query(
        collection(db, 'tb_usuarios'),
        where('condominio_id', '==', condominioId),
        where('papel', 'in', ['morador', 'sindico', 'sub_sindico', 'conselheiro'])
      );
      const querySnapshot = await getDocs(q);
      const mapa = {};
      querySnapshot.forEach((doc) => {
        mapa[doc.id] = doc.data();
      });
      setMoradoresMap(mapa);
    };
    fetchMoradores();
  }, [condominioId]);

  useEffect(() => {
    if (!condominioId || !userRole || !currentUser) return;

    let q;
    if (isAdmin) {
      // Admins veem todas as reclamações do condomínio
      q = query(collection(db, 'tb_reclamacoes'), where('condominio_id', '==', condominioId), orderBy('created_at', 'desc'));
    } else {
      // Moradores veem apenas as suas próprias reclamações
      q = query(collection(db, 'tb_reclamacoes'), where('condominio_id', '==', condominioId), where('autor_id', '==', currentUser.uid), orderBy('created_at', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const reclamacoesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReclamacoes(reclamacoesList);
    }, (error) => {
      console.error("Erro ao buscar reclamações:", error);
    });

    return () => unsubscribe();
  }, [condominioId, userRole, currentUser, isAdmin]);

  const handleCreateReclamacao = async () => {
    if (!novaReclamacao.titulo || !novaReclamacao.descricao) {
      alert('Por favor, preencha o título e a descrição.');
      return;
    }
    if (!currentUser) {
      alert('Você precisa estar logado para registrar uma reclamação.');
      return;
    }
    if (!condominioId) {
      alert('Erro: ID do condomínio não encontrado. Não é possível criar a reclamação.');
      return;
    }

    setUploading(true); // Inicia o estado de carregamento

    try {
      let imageUrl = '';

      // 1. Se uma imagem foi selecionada, faz o upload para o Firebase Storage
      if (imagem) {
        const storage = getStorage();
        // Cria um caminho único para o arquivo para evitar sobreposições
        const imageRef = ref(storage, `reclamacoes/${condominioId}/${Date.now()}-${imagem.name}`);

        const snapshot = await uploadBytes(imageRef, imagem);
        imageUrl = await getDownloadURL(snapshot.ref); // Pega a URL da imagem
      }

      // 2. Salva a reclamação no Firestore com a URL da imagem
      await addDoc(collection(db, 'tb_reclamacoes'), {
        ...novaReclamacao,
        autor_id: currentUser.uid,
        // Adiciona a referência ao condomínio
        condominio_id: condominioId,
        created_at: serverTimestamp(),
        status: 'aberta', // Status inicial
        prioridade: novaReclamacao.prioridade, // Salva a prioridade
        imageUrl: imageUrl, // Salva a URL da imagem
      });
      // O onSnapshot cuidará da atualização da UI
      setNovaReclamacao({ titulo: '', descricao: '' });
      setImagem(null); // Limpa a imagem selecionada
    } catch (error) {
      alert('Erro ao registrar reclamação: ' + error.message);
    } finally {
      setUploading(false); // Finaliza o estado de carregamento
    }
  };

  const handleDeleteReclamacao = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar esta reclamação?")) {
      return;
    }
    try {
      const reclamacaoDocRef = doc(db, 'tb_reclamacoes', id);
      await deleteDoc(reclamacaoDocRef);
      // O onSnapshot cuidará da atualização da UI
      alert('Reclamação deletada com sucesso!');
    } catch (error) {
      alert('Erro ao deletar reclamação: ' + error.message);
    }
  };

  const handleUpdateStatus = async (id, novoStatus) => {
    const reclamacaoRef = doc(db, 'tb_reclamacoes', id);
    try {
      await updateDoc(reclamacaoRef, { status: novoStatus });
      alert('Status atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar status: ' + error.message);
    }
  };

  const handleUpdatePrioridade = async (id, novaPrioridade) => {
    const reclamacaoRef = doc(db, 'tb_reclamacoes', id);
    try {
      await updateDoc(reclamacaoRef, { prioridade: novaPrioridade });
      alert('Prioridade atualizada com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar prioridade: ' + error.message);
    }
  };

  const toggleComentarios = (reclamacaoId) => {
    const isVisible = !comentariosVisiveis[reclamacaoId];
    setComentariosVisiveis({ ...comentariosVisiveis, [reclamacaoId]: isVisible });

    if (isVisible) {
      // Busca os comentários em tempo real
      const comentariosRef = collection(db, 'tb_reclamacoes', reclamacaoId, 'comentarios');
      const q = query(comentariosRef, orderBy('created_at', 'asc'));
      onSnapshot(q, (snapshot) => {
        const commentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setComentarios(prev => ({ ...prev, [reclamacaoId]: commentsList }));
      });
    }
  };

  const handleAddComentario = async (reclamacaoId) => {
    if (!novoComentario.trim()) return;

    const comentariosRef = collection(db, 'tb_reclamacoes', reclamacaoId, 'comentarios');
    try {
      await addDoc(comentariosRef, {
        texto: novoComentario,
        autor_id: currentUser.uid,
        autor_nome: moradoresMap[currentUser.uid]?.nome || 'Usuário',
        created_at: serverTimestamp(),
      });
      setNovoComentario('');
    } catch (error) {
      alert('Erro ao adicionar comentário: ' + error.message);
    }
  };

  // Função para formatar a data
  const formatarData = (timestamp) => {
    if (!timestamp) return 'Data indisponível';
    // Converte o timestamp do Firestore para um objeto Date do JS
    const data = timestamp.toDate();
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(data);
  };

  const reclamacoesFiltradas = reclamacoes.filter(reclamacao => {
    const matchUsuario = filtroUsuario ? reclamacao.autor_id === filtroUsuario : true; // Agora o filtroUsuario é o ID
    const matchStatus = filtroStatus ? reclamacao.status === filtroStatus : true;
    const matchPrioridade = filtroPrioridade ? reclamacao.prioridade === filtroPrioridade : true;
    const matchData = filtroData ?
      reclamacao.created_at && reclamacao.created_at.toDate().toDateString() === filtroData.toDateString()
      : true;

    return matchUsuario && matchStatus && matchPrioridade && matchData;
  });

  const limparFiltros = () => {
    setFiltroUsuario('');
    setFiltroStatus('');
    setFiltroPrioridade('');
    setFiltroData(null);
  };

  // Permite que admins e o autor da reclamação comentem
  const podeComentar = (reclamacao) => {
    if (!currentUser) return false;
    return isAdmin || currentUser.uid === reclamacao.autor_id;
  };

  return (
    <Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 3 }, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh', overflowX: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Reclamações
        </Typography>

        {userRole && ( // Mostra o formulário para qualquer usuário logado com um papel definido
          <Card className={styles.card} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" className={styles.sectionTitle}>Registrar Nova Reclamação</Typography>
              <TextField
                fullWidth
                label="Título"
                value={novaReclamacao.titulo}
                onChange={(e) => setNovaReclamacao({ ...novaReclamacao, titulo: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Box sx={{
                '& .ql-editor': { minHeight: '120px' },
                '& .ql-container': { borderRadius: '0 0 4px 4px' },
                '& .ql-toolbar': {
                  borderRadius: '4px 4px 0 0',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  flexWrap: 'wrap' // Permite que a toolbar quebre linha
                },
                mb: 2,
                maxWidth: '100%' // Garante que não ultrapasse a largura do card
              }}>
                <ReactQuill
                  theme="snow"
                  value={novaReclamacao.descricao}
                  onChange={(content) => setNovaReclamacao({ ...novaReclamacao, descricao: content })}
                  style={{ backgroundColor: 'white', color: 'black' }}
                />
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={novaReclamacao.prioridade}
                  label="Prioridade"
                  onChange={(e) => setNovaReclamacao({ ...novaReclamacao, prioridade: e.target.value })}
                >
                  {prioridades.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {!imagem ? (
                  <Button variant="outlined" component="label">
                    Anexar Foto
                    <input type="file" hidden accept="image/*" onChange={(e) => setImagem(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip
                    label={imagem.name}
                    onDelete={() => setImagem(null)}
                  />
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateReclamacao}
                  disabled={uploading}
                  sx={{ ml: 'auto' }}
                >
                  {uploading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Reclamação'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Lista de Reclamações</Typography>

            <Grid container spacing={1} sx={{ mb: 3 }} alignItems="center">
              {isAdmin && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filtrar por Usuário</InputLabel>
                    <Select value={filtroUsuario} label="Filtrar por Usuário" onChange={(e) => setFiltroUsuario(e.target.value)}>
                      <MenuItem value=""><em>Todos</em></MenuItem>
                      {Object.values(moradoresMap).map(morador => (
                        <MenuItem key={morador.email} value={Object.keys(moradoresMap).find(key => moradoresMap[key].email === morador.email)}>{morador.nome}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={isAdmin ? 2.5 : 4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrar por Status</InputLabel>
                  <Select value={filtroStatus} label="Filtrar por Status" onChange={(e) => setFiltroStatus(e.target.value)}>
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={isAdmin ? 2.5 : 4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrar por Prioridade</InputLabel>
                  <Select value={filtroPrioridade} label="Filtrar por Prioridade" onChange={(e) => setFiltroPrioridade(e.target.value)}>
                    <MenuItem value=""><em>Todas</em></MenuItem>
                    {prioridades.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={isAdmin ? 2 : 3}>
                <DatePicker
                  label="Filtrar por Data"
                  value={filtroData}
                  onChange={(newValue) => setFiltroData(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={isAdmin ? 1 : 1}>
                <Button fullWidth variant="text" onClick={limparFiltros}>Limpar</Button>
              </Grid>
            </Grid>

            <List>
              {reclamacoesFiltradas.map((reclamacao) => (
                <ListItem
                  key={reclamacao.id}
                  divider
                  sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}
                >
                  <ListItemText
                    primary={reclamacao.titulo}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={(
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Aberto por: {moradoresMap[reclamacao.autor_id]?.nome || 'Desconhecido'} em {formatarData(reclamacao.created_at)}
                        </Typography>
                        <Typography
                          component="div"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reclamacao.descricao) }}
                          sx={{ my: 1, wordBreak: 'break-word' }} />
                        {reclamacao.imageUrl && <img src={reclamacao.imageUrl} alt="Imagem da reclamação" style={{ width: '100%', maxWidth: '400px', marginTop: '10px', borderRadius: '8px' }} />}

                        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }} alignItems="center">
                          {isAdmin ? (
                            <>
                              <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 150 } }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                  value={reclamacao.status}
                                  label="Status"
                                  onChange={(e) => handleUpdateStatus(reclamacao.id, e.target.value)}
                                >
                                  {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 150 } }}>
                                <InputLabel>Prioridade</InputLabel>
                                <Select
                                  value={reclamacao.prioridade || 'Baixa'}
                                  label="Prioridade"
                                  onChange={(e) => handleUpdatePrioridade(reclamacao.id, e.target.value)}
                                >
                                  {prioridades.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </Select>
                              </FormControl>
                            </>
                          ) : (
                            <>
                              <Chip
                                label={`Status: ${reclamacao.status}`}
                                size="small"
                                color={
                                  reclamacao.status === 'Finalizada' ? 'success' :
                                    reclamacao.status === 'Cancelada' ? 'error' :
                                      reclamacao.status === 'Em Análise' ? 'warning' :
                                        'default'
                                }
                              />
                              <Chip
                                label={`Prioridade: ${reclamacao.prioridade || 'Baixa'}`}
                                size="small"
                                color={reclamacao.prioridade === 'Alta' ? 'error' : reclamacao.prioridade === 'Média' ? 'warning' : 'default'}
                              />
                            </>
                          )}
                          <Button size="small" onClick={() => toggleComentarios(reclamacao.id)} disabled={!podeComentar(reclamacao)}>
                            {comentariosVisiveis[reclamacao.id] ? 'Ocultar' : 'Responder'}
                          </Button>
                        </Stack>

                        <Collapse in={comentariosVisiveis[reclamacao.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: '8px' }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Comentários</Typography>
                            <List>
                              {(comentarios[reclamacao.id] || []).map(comment => (
                                <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0 }}>
                                  <ListItemText
                                    primary={comment.autor_nome}
                                    secondary={
                                      <>
                                        <Typography
                                          component="div"
                                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.texto) }}
                                          sx={{ wordBreak: 'break-word' }}
                                        />
                                        <Typography variant="caption" color="text.secondary">{formatarData(comment.created_at)}</Typography>
                                      </>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                            {podeComentar(reclamacao) && (
                              <Box sx={{ display: 'flex', mt: 2, flexDirection: 'column' }}>
                                <Box sx={{
                                  flexGrow: 1,
                                  '& .ql-editor': { minHeight: '80px' },
                                  '& .ql-container': { borderRadius: '0 0 4px 4px' },
                                  '& .ql-toolbar': { borderRadius: '4px 4px 0 0', backgroundColor: '#f5f5f5', flexWrap: 'wrap' },
                                  mb: 1
                                }}>
                                  <ReactQuill
                                    theme="snow"
                                    value={novoComentario}
                                    onChange={setNovoComentario}
                                    style={{ backgroundColor: 'white', color: 'black' }}
                                  />
                                </Box>
                                <Button variant="contained" onClick={() => handleAddComentario(reclamacao.id)} fullWidth>
                                  Enviar
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </>
                    )}
                    sx={{ width: '100%', pr: { sm: 2 } }}
                  />
                  {isAdmin && (
                    <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, mt: { xs: 1, sm: 0 } }}>
                      <IconButton aria-label="delete" onClick={() => handleDeleteReclamacao(reclamacao.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Reclamacoes;